import { validate as isUUID } from 'uuid'
import { useState } from 'react'
import { useLocation, useParams } from 'wouter'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useEndSession } from '../hooks/useEndSession'
import { useCreateSessionEvent } from '../hooks/useCreateSessionEvent'
import { useTechnicianPageStore } from '../hooks/useTechnicianPageStore'
import { useTechnicianDocument } from '../hooks/useTechnicianDocument'
import { findSubPageIndex } from '@/pages/TechnicianPage/fns'

export const CompleteSessionButton = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [, setLocation] = useLocation()
  const { projectId, documentId } = useParams<{
    projectId: string
    documentId: string
  }>()

  const currentSessionId = useTechnicianPageStore(
    (state) => state.currentSessionId,
  )
  const currentTechnician = useTechnicianPageStore(
    (state) => state.currentTechnician,
  )

  const { endSession } = useEndSession()
  const { createSessionEvent } = useCreateSessionEvent()
  const { currentPage } = useTechnicianDocument()

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger
        className="inline-flex items-center space-x-2 justify-center whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary-50 text-white shadow hover:opacity-90 h-10 px-8 rounded-full"
        data-testid="technician-mode-complete-session-button"
      >
        <CheckCircleIcon className="h-5 w-5" />
        <span>Complete Session</span>
      </DialogTrigger>
      <DialogContent
        ariaLabel="Leave Technician mode"
        className="max-w-lg min-h-0"
      >
        <DialogHeader className="space-y-0">
          <DialogTitle className="text-center mt-2 mb-0">
            Are you sure want to complete the session?
          </DialogTitle>
          <DialogDescription className="flex flex-1"></DialogDescription>
        </DialogHeader>

        <div className="flex justify-center mt-6">
          <Button
            className="flex items-center mx-5 rounded-full"
            variant="secondary"
            onClick={() => {
              setIsOpen(false)
            }}
          >
            Cancel
          </Button>
          <Button
            className="flex items-center mx-5 rounded-full"
            onClick={() => {
              if (
                currentTechnician &&
                currentSessionId &&
                currentPage &&
                currentPage.id
              ) {
                if (isUUID(currentPage.id)) {
                  createSessionEvent({
                    documentPageId: currentPage.id,
                    buttonLabel: 'Complete',
                    completedTimestamp: new Date().toISOString(),
                    subPageIndex: findSubPageIndex(currentPage),
                    onSuccess: () => {
                      endSession()
                    },
                  })
                } else {
                  endSession()
                }
              }
              setLocation(`~/p/${projectId}/document/${documentId}/edit`)
            }}
            data-testid="technician-mode-confirm-complete-session-button"
          >
            Complete
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
